import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";

interface BlogPostContent {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
  content: string;
}

const BLOG_CONTENT: Record<string, BlogPostContent> = {
  "echonour-vs-descript": {
    slug: "echonour-vs-descript",
    title: "Echonour vs Descript: Which Transcription Tool Is Right for You?",
    description: "A detailed comparison of Echonour and Descript — two different approaches to transcription. One is a focused transcription tool, the other a full video editing suite.",
    date: "2026-02-23",
    readTime: "7 min read",
    category: "Comparison",
    content: `
## Overview: Two Very Different Tools

If you're looking for transcription software, you've probably come across both Echonour and Descript. While both can transcribe audio, they serve fundamentally different purposes.

Echonour is a focused transcription tool built specifically for podcasts and YouTube videos. It does one thing exceptionally well: turns audio/video into accurate, speaker-identified text.

Descript is a full-featured audio and video editing suite that happens to include transcription. It's a powerful creative tool for content producers who need to edit, enhance, and publish media.

The question isn't "which is better" — it's "which is right for your workflow?"

## Feature Comparison

### Transcription Accuracy

Both tools use state-of-the-art speech recognition and deliver similar baseline accuracy (95%+ for clear English audio).

| Feature | Echonour | Descript |
|---------|----------|----------|
| Base accuracy | ~95% | ~95% |
| Languages | English-focused | 25 languages |
| Custom vocabulary | Coming soon | Yes (glossary) |

Winner: Tie for English. Descript wins for multi-language support.

### Speaker Identification

This is where the tools diverge significantly.

Echonour's approach:
- Automatically identifies speakers by actual name (not just "Speaker 1")
- Uses video metadata, titles, and AI analysis to name speakers
- Learns host/guest patterns across episodes
- Displays clear attribution: "Tim Ferriss" and "Naval Ravikant" instead of generic labels

Descript's approach:
- Detects up to 8+ speakers
- Labels them as Speaker 1, Speaker 2, etc.
- "Speaker Detective" plays clips to help you manually name speakers
- Multitrack transcription for separate audio tracks

Winner: Echonour for automatic naming. Descript for multi-track recording workflows.

### Input Options

| Input Type | Echonour | Descript |
|------------|----------|----------|
| YouTube URL | ✅ Direct paste | ❌ Download first |
| Audio file upload | ✅ | ✅ |
| Video file upload | ✅ | ✅ |
| RSS feed URL | ✅ | ❌ |
| Screen recording | ❌ | ✅ Built-in |
| Remote recording | ❌ | ✅ (Rooms feature) |

Winner: Echonour for web content (YouTube, podcasts). Descript for original recording.

### Editing Capabilities

Here's where Descript truly shines — and where the tools are incomparable.

Echonour: Focused on transcript output
- Clean, formatted transcripts
- Clickable timestamps
- AI-generated summaries
- Export to text

Descript: Full editing suite
- Edit audio/video by editing text (delete text = delete audio)
- Remove filler words ("um", "uh", "like") automatically
- Overdub — AI voice cloning to fix mistakes
- Green screen removal
- Eye contact correction
- Studio sound enhancement
- Dynamic captions
- Video generation
- Screen recording with editing

Winner: Descript, hands down. If you need editing, that's the tool.

### Export Options

| Format | Echonour | Descript |
|--------|----------|----------|
| Plain text (.txt) | ✅ | ✅ |
| Copy to clipboard | ✅ | ✅ |
| Shareable link | ✅ | ✅ |
| Video export | ❌ | ✅ (720p-4K) |
| Audio export | ❌ | ✅ |
| SRT subtitles | Coming soon | ✅ |
| Timeline export | ❌ | ✅ |

Winner: Descript for content creators. Echonour for pure transcription.

## Pricing Comparison

This is where the choice becomes clearer for many users.

### Echonour Pricing

| Plan | Price | Transcription |
|------|-------|---------------|
| Free | $0/month | 20 hours |
| Pro | $12/month | Unlimited |

Simple, transparent, no media minute limits.

### Descript Pricing

| Plan | Price | Media Hours |
|------|-------|-------------|
| Free | $0/month | 1 hour |
| Hobbyist | $16/month | 10 hours |
| Creator | $24/month | 30 hours |
| Business | $40/month | 40 hours |

Descript's pricing includes their full editing suite, but transcription is metered by "media minutes."

### Cost Analysis: If You Just Need Transcription

Let's say you transcribe 15 hours of podcast content per month:

- Echonour Pro: $12/month (unlimited)
- Descript Creator: $24/month (30 hours included)

For heavy transcription use (30+ hours/month):

- Echonour Pro: $12/month (still unlimited)
- Descript: You'd need to buy additional media minutes or upgrade

Winner: Echonour for pure transcription value. Descript if you need the editing suite.

## Pros and Cons

### Echonour

Pros:
- ✅ Automatic speaker naming (actual names, not "Speaker 1")
- ✅ YouTube URL support — paste and transcribe
- ✅ RSS feed support for podcasts
- ✅ Simpler interface — no learning curve
- ✅ More affordable for transcription-only use
- ✅ Unlimited transcription on Pro plan
- ✅ AI summaries included

Cons:
- ❌ No audio/video editing
- ❌ English-focused (limited multi-language)
- ❌ No built-in recording
- ❌ No export to SRT yet

### Descript

Pros:
- ✅ Full audio/video editing suite
- ✅ Remove filler words automatically
- ✅ Overdub (AI voice correction)
- ✅ Green screen, eye contact, studio sound
- ✅ Built-in screen recording and remote recording
- ✅ 25-language transcription
- ✅ Subtitle/caption generation
- ✅ Video generation features

Cons:
- ❌ More expensive for transcription-only use
- ❌ Media minutes are metered
- ❌ Steeper learning curve
- ❌ No direct YouTube URL support
- ❌ Generic speaker labels (manual naming required)
- ❌ Desktop app required for full features

## Who Should Choose Which?

### Choose Echonour if you:

- Want transcripts from YouTube videos or podcasts — paste URL, get transcript
- Need automatic speaker naming — "Joe Rogan" not "Speaker 1"
- Don't need to edit the source audio/video — just want the text
- Transcribe frequently — unlimited plan is better value
- Want a simple tool — no complex interface to learn
- Are a researcher, student, or writer — extracting quotes and insights

### Choose Descript if you:

- Create podcasts or videos — need to edit and publish
- Want to remove filler words — clean up rough recordings
- Need voice cloning (Overdub) — fix mistakes without re-recording
- Record screencasts or remote interviews — built-in recording tools
- Publish with captions — integrated captioning workflow
- Work in multiple languages — 25-language support
- Want one tool for everything — transcription is just one part of your workflow

## The Honest Take

Descript is a fantastic product. If you're a content creator who records, edits, and publishes audio or video, it's genuinely impressive. The text-based editing paradigm is revolutionary.

But Descript has transcription as a *feature*. Echonour has transcription as its *purpose*.

If you download YouTube videos and load them into Descript just to get a transcript, you're:
1. Paying more than necessary
2. Using 10% of the tool
3. Missing out on automatic speaker naming

If you're a podcaster editing your own shows, Descript makes perfect sense. The filler word removal alone is worth it.

Our recommendation:

- Need transcripts from existing content → Echonour
- Create and edit content → Descript
- Both → Use Echonour for research/transcription, Descript for production

## Conclusion

Echonour and Descript aren't really competitors — they're complementary tools for different jobs.

Echonour is the scalpel: precise, focused, does one thing exceptionally well. Best for turning existing audio/video into accurate, speaker-identified transcripts.

Descript is the Swiss Army knife: does many things, all of them well. Best for creators who need the full audio/video production toolkit.

Choose based on what you're actually trying to accomplish, not which tool has more features.

---

Ready to try focused transcription? [Get started with Echonour free →](/)
    `,
  },
  "how-to-transcribe-youtube-video": {
    slug: "how-to-transcribe-youtube-video",
    title: "How to Transcribe a YouTube Video (With Timestamps)",
    description: "Learn how to get accurate transcripts from any YouTube video, complete with timestamps and speaker identification. No software download required.",
    date: "2026-02-23",
    readTime: "5 min read",
    category: "Tutorial",
    content: `
## Why Transcribe YouTube Videos?

Whether you're a researcher citing podcast interviews, a student reviewing lecture content, or a content creator repurposing video, having an accurate transcript is invaluable.

Common use cases:
- Academic research — cite specific quotes with timestamps
- Content repurposing — turn videos into blog posts or social media
- Accessibility — make content available to deaf/HoH audiences
- SEO — search engines can't watch videos, but they can read transcripts
- Note-taking — quickly find key moments without rewatching

## The Problem with YouTube's Auto-Captions

YouTube's automatic captions are... okay. But they have significant issues:

1. No speaker identification — who said what?
2. Poor punctuation — makes reading difficult
3. No paragraph breaks — wall of text
4. Can't export easily — locked in YouTube's interface
5. Accuracy issues — especially with names, technical terms, accents

## How to Get a Better Transcript with Echonour

### Step 1: Copy the YouTube URL

Find the video you want to transcribe. Copy the URL from your browser's address bar. It should look like:
- \`https://www.youtube.com/watch?v=XXXXXXXXXXX\`
- \`https://youtu.be/XXXXXXXXXXX\`

Both formats work.

### Step 2: Paste into Echonour

Go to [echonour.com](https://echonour.com) and paste the URL into the transcription box. Click "Transcribe."

### Step 3: Wait for Processing

Transcription typically takes 2-5 minutes regardless of video length. You'll see a progress indicator. The transcript appears in your library when complete.

### Step 4: Review Your Transcript

Your transcript includes:
- Speaker names — automatically identified from video metadata
- Timestamps — click any timestamp to jump to that moment
- Clean formatting — proper punctuation and paragraphs
- AI summary — get the key points at a glance

## Tips for Best Results

Video quality matters. Clear audio produces better transcripts. Videos with:
- Single speaker or clear turn-taking
- Minimal background noise
- Standard accents

...will produce the most accurate results.

For interviews and podcasts, Echonour automatically identifies hosts and guests by name when possible, using video metadata and AI analysis.

## What About Long Videos?

Echonour handles videos of any length. A 3-hour podcast transcribes just as easily as a 5-minute clip. Processing time is roughly the same thanks to parallel processing.

## Export Options

Once transcribed, you can:
- Copy to clipboard — paste anywhere
- Download as text — plain .txt file
- Share link — send the transcript URL to others

## Frequently Asked Questions

Is it free?
Yes! New users get 20 hours of free transcription. No credit card required.

What about private/unlisted videos?
We can only transcribe videos that are publicly accessible. Private videos won't work.

How accurate is it?
Typically 95%+ for clear English audio. Accuracy varies with audio quality and accents.

---

Ready to transcribe your first video? [Get started free →](/)
    `,
  },
  "how-to-transcribe-podcast-episode": {
    slug: "how-to-transcribe-podcast-episode",
    title: "How to Transcribe a Podcast Episode in Minutes",
    description: "Turn any podcast into searchable text. Works with RSS feeds, direct audio URLs, and uploaded files. Get speaker names automatically identified.",
    date: "2026-02-23",
    readTime: "4 min read",
    category: "Tutorial",
    content: `
## Why Transcribe Podcasts?

Podcasts are incredible for learning, but they're terrible for reference. You can't search audio. You can't skim. You can't quote without re-listening.

A transcript changes everything:
- Search for topics — find that one insight instantly
- Quote accurately — cite with confidence
- Read faster — skim an hour of content in minutes
- Create content — turn episodes into articles, threads, clips

## Three Ways to Transcribe a Podcast

### Method 1: YouTube URL (Easiest)

Many podcasts publish video versions on YouTube. If available, this is the fastest method:

1. Find the episode on YouTube
2. Copy the URL
3. Paste into Echonour
4. Done!

Speaker names are often identified automatically from the video title and description.

### Method 2: Direct Audio URL

If you have a direct link to the audio file (ending in .mp3, .m4a, etc.), you can paste that directly:

1. Find the audio file URL (often in RSS feed or right-click → "Copy audio address")
2. Paste into Echonour
3. Transcribe

### Method 3: RSS Feed URL

For podcast power users: paste the RSS feed URL and Echonour will show you recent episodes to choose from.

1. Find the podcast's RSS feed URL
2. Paste into Echonour
3. Select the episode you want
4. Transcribe

## Speaker Identification for Podcasts

This is where Echonour shines. Instead of generic "Speaker 1" and "Speaker 2" labels, you get actual names:

Before (typical transcription):
> Speaker 1: Welcome to the show. Today we're talking about...
> Speaker 2: Thanks for having me.

After (Echonour):
> Tim Ferriss: Welcome to the show. Today we're talking about...
> Naval Ravikant: Thanks for having me.

How? Echonour analyzes:
- Episode title and description
- Host patterns across episodes
- Voice profiles from previous transcriptions
- AI-powered speaker analysis

The more you transcribe from the same podcast, the better the speaker identification gets.

## Best Podcasts for Transcription

Echonour works great with:
- Interview podcasts — clear turn-taking, identifiable speakers
- Educational content — Huberman Lab, Lex Fridman, etc.
- Business podcasts — All-In, My First Million, etc.
- News podcasts — NPR, The Daily, etc.

## Tips for Podcast Transcription

Transcribe the whole series. Speaker identification improves as Echonour learns host voices.

Use the AI summary. Long podcasts (2-3 hours) generate detailed summaries highlighting key topics.

Search your library. Once transcribed, all your podcasts become searchable from one place.

## Common Questions

Can I transcribe Spotify podcasts?
Not directly from Spotify. Look for the same episode on YouTube or find the RSS feed.

What about Apple Podcasts?
Same deal — find the RSS feed or YouTube version.

How do I find an RSS feed?
Search "[podcast name] RSS feed" or check the podcast's website. Tools like ListenNotes also show RSS URLs.

---

Ready to transcribe your favorite podcast? [Start free →](/)
    `,
  },
  "how-to-get-timestamps-in-transcription": {
    slug: "how-to-get-timestamps-in-transcription",
    title: "Getting Timestamps in Your Transcriptions: A Complete Guide",
    description: "Why timestamps matter for citations, navigation, and accessibility. Learn how to get precise timestamps in every transcript.",
    date: "2026-02-23",
    readTime: "4 min read",
    category: "Guide",
    content: `
## Why Timestamps Matter

Timestamps transform a transcript from a wall of text into a navigable document. They enable:

### 1. Academic Citations

When citing a podcast or video in academic work, timestamps let readers verify your quotes:

> "The key insight is that attention is all you need" (Vaswani, 2023, 14:32)

Without timestamps, citations are unverifiable.

### 2. Content Navigation

Click a timestamp to jump directly to that moment in the audio/video. No more scrubbing through hours of content to find one quote.

### 3. Clip Creation

Know exactly where to cut. Timestamps tell you the precise start and end points for creating short clips.

### 4. Meeting Notes

"Action item discussed at 23:45" is infinitely more useful than "somewhere in the middle."

### 5. Accessibility

Screen reader users can navigate by timestamp. Deaf/HoH viewers can correlate text with video playback.

## Types of Timestamps

### Segment-Level Timestamps

The most common format. Each paragraph or speaker turn gets a timestamp:

> [00:14:32] Host: So tell me about your early career...
> 
> [00:14:38] Guest: I started in investment banking...

This is what Echonour provides by default.

### Word-Level Timestamps

Every word has precise timing. Essential for:
- Karaoke-style highlighting during playback
- Precise clip boundaries
- Subtitle generation

Echonour supports word-level timestamps for audio playback highlighting.

## How Echonour Handles Timestamps

Every Echonour transcript includes:

1. Segment timestamps — shown next to each speaker turn
2. Clickable navigation — click any timestamp to jump to that moment
3. Word-level precision — for smooth playback highlighting
4. Export with timestamps — keeps timing in downloaded transcripts

## Timestamp Formats

Different contexts need different formats:

| Format | Example | Use Case |
|--------|---------|----------|
| HH:MM:SS | 01:23:45 | Long content (>1hr) |
| MM:SS | 23:45 | Short content |
| Seconds | 1425 | Technical/API use |
| SRT | 00:23:45,000 | Subtitles |

Echonour displays human-readable formats (HH:MM:SS or MM:SS depending on length) and exports in standard formats.

## Tips for Working with Timestamps

Plan your clips. Before transcribing, know that you'll want to extract segments. Timestamps make this trivial.

Use search + timestamps. Search for a keyword, get the timestamp, jump directly there.

Cite properly. Include timestamps in any quotes from audio/video sources.

## Common Questions

How accurate are the timestamps?
Within 1-2 seconds of the actual audio. Good enough for citation and navigation.

Can I adjust timestamps?
Not in Echonour currently. The timestamps are generated by the speech recognition engine.

Do timestamps work with audio playback?
Yes! When playing audio in Echonour, the current position highlights in the transcript.

---

Get timestamps in your next transcript. [Try Echonour free →](/)
    `,
  },
  "echonour-vs-rev": {
    slug: "echonour-vs-rev",
    title: "Echonour vs Rev: An Honest Comparison for 2026",
    description: "Comparing Echonour and Rev for transcription — pricing, accuracy, speaker identification, and when to choose each. A fair look at two different approaches.",
    date: "2026-02-23",
    readTime: "8 min read",
    category: "Comparison",
    content: `
## Overview: Two Different Transcription Approaches

Rev and Echonour both convert audio to text, but they've evolved in different directions. Understanding these differences helps you choose the right tool for your needs.

Rev is one of the original transcription services, famous for human-powered transcription. They've since added AI transcription and pivoted to a subscription model targeting legal, business, and media professionals.

Echonour is a focused AI transcription tool built specifically for podcasts and YouTube videos. It emphasizes automatic speaker naming, direct URL support, and simplicity.

Let's break down how they compare.

## Feature Comparison

### Transcription Accuracy

Both services use modern AI for automated transcription with similar baseline accuracy.

| Feature | Echonour | Rev |
|---------|----------|-----|
| AI accuracy | ~95% | ~95% |
| Human option | No | Yes (add-on) |
| Languages | English-focused | 37+ languages |
| Verbatim mode | Standard | Available on Pro |

Winner: Rev for multi-language support and human transcription option. Tie for AI accuracy on clear English audio.

The honest truth about human transcription: Rev's human transcription is genuinely superior for:
- Heavy accents
- Mumbled speech
- Technical jargon
- Background noise
- Multiple overlapping speakers

If your audio quality is poor, human transcription from Rev (or similar services) will outperform any AI — including ours.

### Speaker Identification

This is where the tools diverge significantly.

Echonour's approach:
- Identifies speakers by actual name automatically
- Uses video titles, descriptions, and AI analysis
- Labels as "Joe Rogan" and "Elon Musk" — not "Speaker 1"
- Learns patterns across podcast episodes

Rev's approach:
- AI detects multiple speakers
- Labels as Speaker 1, Speaker 2, etc.
- Human transcription can add names if specified
- No automatic name detection

Winner: Echonour for automatic speaker naming. Rev's human service if you specifically request names.

### Input Options

| Input Type | Echonour | Rev |
|------------|----------|-----|
| YouTube URL | ✅ Direct paste | ❌ Upload required |
| Audio file upload | ✅ | ✅ |
| Video file upload | ✅ | ✅ |
| RSS feed URL | ✅ | ❌ |
| Zoom/Teams/Meet | ❌ | ✅ AI Notetaker |
| API access | Coming soon | ✅ |

Winner: Echonour for web content (YouTube, podcasts). Rev for meeting integrations and API needs.

### Turnaround Time

| Service | AI Transcription | Human Transcription |
|---------|------------------|---------------------|
| Echonour | 2-5 minutes | N/A |
| Rev AI | Near real-time | N/A |
| Rev Human | N/A | 24-48 hours typically |

Both AI services are fast. If you need human accuracy for difficult audio, expect to wait.

Winner: Tie for AI. Rev wins by default for human (Echonour doesn't offer it).

### Export Formats

| Format | Echonour | Rev |
|--------|----------|-----|
| Plain text | ✅ | ✅ |
| Word doc | ❌ | ✅ |
| SRT subtitles | Coming soon | ✅ |
| Copy to clipboard | ✅ | ✅ |
| Shareable link | ✅ | ✅ |

Winner: Rev has more export options currently.

## Pricing Comparison

*Last verified: February 2026*

### Echonour Pricing

| Plan | Price | Transcription |
|------|-------|---------------|
| Free | $0/month | 45 minutes |
| Pro | $12/month | Unlimited |

Simple and predictable. Pay once, transcribe as much as you want.

### Rev Pricing (Current Model)

Rev has moved to a subscription model:

| Plan | Price (Annual) | AI Minutes/Month |
|------|----------------|------------------|
| Free | $0 | 45 minutes |
| Essentials | $25.49/seat/month | 5,000 minutes |
| Pro | $47.99/seat/month | 10,000 minutes |
| Unlimited | Custom | Unlimited |

Human transcription is now an add-on with subscriber discounts (3-15% off depending on plan).

### Cost Analysis

Light user (2-3 hours/month):
- Echonour Free: $0
- Rev Free: $0
- Verdict: Both work

Regular user (10+ hours/month):
- Echonour Pro: $12/month (unlimited)
- Rev Essentials: $25.49/month (5,000 min = 83 hours)
- Verdict: Echonour is cheaper for pure transcription

Heavy user (50+ hours/month):
- Echonour Pro: $12/month (still unlimited)
- Rev Pro: $47.99/month (10,000 min = 166 hours)
- Verdict: Echonour significantly cheaper

Occasional human transcription needs:
- Echonour: Not available
- Rev: Add-on pricing with subscriber discounts
- Verdict: Rev wins by default

### The Real Cost Difference

If you transcribe podcasts and YouTube videos regularly, Echonour's unlimited plan at $12/month is hard to beat. Rev's subscription model includes more features (AI notetaker, legal templates, etc.) but costs 2-4x more.

If you occasionally need human transcription for difficult audio, Rev's hybrid model makes sense.

## Pros and Cons

### Echonour

Pros:
- ✅ Automatic speaker naming (actual names, not labels)
- ✅ YouTube URL support — paste and go
- ✅ RSS feed support for podcast series
- ✅ Simpler, focused interface
- ✅ $12/month unlimited transcription
- ✅ Generous free tier (45 minutes/month)

Cons:
- ❌ No human transcription option
- ❌ English-focused (limited multi-language)
- ❌ No meeting bot integration
- ❌ No API access yet
- ❌ Fewer export formats

### Rev

Pros:
- ✅ Human transcription available (best for difficult audio)
- ✅ 37+ language support
- ✅ AI Notetaker for Zoom/Teams/Meet
- ✅ More export formats (Word, SRT, etc.)
- ✅ API access for developers
- ✅ Legal-specific features (templates, CJIS compliance)
- ✅ Established reputation since 2010

Cons:
- ❌ More expensive for transcription-only use
- ❌ No direct YouTube URL support
- ❌ Generic speaker labels (Speaker 1, Speaker 2)
- ❌ Complex pricing structure
- ❌ Per-seat pricing adds up for teams

## Who Should Choose Which?

### Choose Echonour if you:

- Transcribe YouTube videos or podcasts — paste URL, get transcript
- Want automatic speaker naming — "Naval Ravikant" not "Speaker 1"  
- Transcribe frequently — unlimited at $12/month beats per-minute pricing
- Want simplicity — no learning curve, just transcription
- Are a researcher, student, or content creator — extracting quotes and insights
- Have clear, quality audio — AI handles it well

### Choose Rev if you:

- Need human transcription — for difficult audio, accents, or legal accuracy
- Work in multiple languages — 37+ languages supported
- Need meeting integrations — AI Notetaker for Zoom/Teams/Meet
- Are in legal/medical fields — HIPAA and CJIS compliance options
- Need API access — build transcription into your workflow
- Have complex compliance requirements — enterprise security features

## The Honest Take

Rev pioneered the transcription industry and their human transcription is genuinely excellent. If you have a recording with heavy accents, multiple overlapping speakers, or poor audio quality, Rev's human transcribers will outperform any AI — including ours.

But here's the reality for most users:

If your audio is reasonably clear (which most podcasts and YouTube videos are), AI transcription is "good enough" — and it's instant and cheap.

The question becomes: do you pay $25-50/month for features you might not need, or $12/month for focused transcription?

For podcasts and YouTube specifically, Echonour's automatic speaker naming is a genuine differentiator. Getting "Tim Ferriss" and "Peter Attia" instead of "Speaker 1" and "Speaker 2" saves real time.

Our recommendation:

- Clear audio + YouTube/podcasts → Echonour
- Difficult audio + accuracy critical → Rev human transcription
- Meeting recordings + integrations → Rev
- Budget-conscious + high volume → Echonour

## Conclusion

Rev and Echonour serve overlapping but distinct markets.

Rev is the full-service transcription platform — AI and human options, meeting integrations, enterprise features, multi-language support. It's comprehensive but costs more.

Echonour is the focused tool — paste a YouTube URL, get a transcript with named speakers, pay $12/month for unlimited. It's simple but limited.

Neither is objectively "better." Rev wins for difficult audio, compliance needs, and meetings. Echonour wins for podcasts, YouTube, speaker naming, and value.

Choose based on what you're actually transcribing.

---

Ready to try focused podcast transcription? [Get started with Echonour free →](/)
    `,
  },
  "how-to-identify-speakers-in-transcription": {
    slug: "how-to-identify-speakers-in-transcription",
    title: "Automatic Speaker Identification: How AI Names Your Speakers",
    description: "Discover how AI-powered speaker diarization identifies who said what, and how Echonour goes further by naming speakers automatically.",
    date: "2026-02-23",
    readTime: "5 min read",
    category: "Guide",
    content: `
## The Speaker Identification Problem

Raw transcription is just text. It doesn't know who said what.

Without speaker identification:
> Welcome to the show. Thanks for having me. So tell me about your journey. Well, it started in 2015...

With speaker identification:
> Host: Welcome to the show.
> Guest: Thanks for having me.
> Host: So tell me about your journey.
> Guest: Well, it started in 2015...

This is called speaker diarization — figuring out "who spoke when."

## How Speaker Diarization Works

Modern speech recognition uses AI to:

1. Detect voice changes — identify when a different person starts speaking
2. Cluster similar voices — group segments by the same speaker
3. Label speakers — assign Speaker 1, Speaker 2, etc.

This works surprisingly well for 2-3 speakers with clear turn-taking. It struggles with:
- Crosstalk (people talking over each other)
- Similar voices (identical twins)
- Large groups (5+ speakers)
- Poor audio quality

## From "Speaker 1" to Real Names

Basic diarization gives you "Speaker 1" and "Speaker 2." Useful, but not ideal.

Echonour goes further with automatic speaker naming:

### Step 1: Metadata Analysis

We parse the video/podcast title and description for names:
- "Tim Ferriss interviews Naval Ravikant"
- "Guest: Dr. Andrew Huberman"
- "Featuring Chamath Palihapitiya"

### Step 2: Pattern Recognition

Common patterns indicate roles:
- First speaker in an interview → likely the host
- "Thanks for having me" → likely the guest
- Intro music followed by speaking → host opening

### Step 3: Voice Profile Matching

For podcasts you've transcribed before, Echonour recognizes returning speakers:
- Joe Rogan has a distinct voice profile
- The All-In hosts are individually recognizable
- Your favorite interviewer gets named automatically

### Step 4: AI Synthesis

When metadata + patterns + voice profiles aren't enough, AI analyzes the conversation content to make educated guesses.

## Accuracy Expectations

Best case (well-known podcast): 95%+ accuracy on speaker names
- Famous hosts get recognized
- Video description names the guest
- Clear two-person interview format

Average case (unknown podcast): 80%+ accuracy
- Speakers correctly separated
- Generic names (Host/Guest) or first names from metadata
- May need manual correction for unusual names

Challenging cases:
- Panel discussions with 4+ speakers
- No metadata about participants
- Heavy crosstalk or interruptions

## Tips for Better Speaker Identification

Use YouTube when available. Video descriptions typically name guests.

Transcribe consistently. The more episodes from a podcast, the better the voice recognition.

Check the first few transcripts. Early transcripts may need manual name corrections. This trains the system for future episodes.

## How to Correct Speaker Names

If a speaker is misidentified:
1. Open the transcript
2. Click on a speaker name
3. Edit to the correct name
4. Future transcripts from the same source improve

## Speaker Identification for Different Content Types

### Podcasts
Best results. Hosts are recognizable, guests are named in descriptions.

### YouTube Videos
Good results. Titles often include speaker names.

### Uploaded Meetings
Variable. No metadata to work with, relies purely on voice separation.

### Lectures
Single speaker = trivial. Multi-speaker Q&A is harder.

## The Future of Speaker Identification

As voice AI improves, expect:
- Better handling of crosstalk
- More accurate voice fingerprinting
- Real-time speaker identification
- Integration with public figure databases

---

Experience automatic speaker naming. [Try Echonour free →](/)
    `,
  },
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? BLOG_CONTENT[slug] : null;

  useEffect(() => {
    if (post) {
      document.title = `${post.title} - Echonour Blog`;
      
      // Inject JSON-LD for the blog post
      const jsonld = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.description,
        "datePublished": post.date,
        "dateModified": post.date,
        "author": {
          "@type": "Organization",
          "name": "Echonour"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Echonour",
          "url": "https://echonour.com"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://echonour.com/blog/${post.slug}`
        }
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonld);
      script.id = 'blogpost-jsonld';
      document.head.appendChild(script);
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.description);
      }
      
      return () => {
        const existing = document.getElementById('blogpost-jsonld');
        if (existing) existing.remove();
      };
    }
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50 dark:bg-gray-900">
      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {/* Back to blog */}
        <Link
          to="/blog"
          className="mb-8 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {post.category}
            </span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {post.description}
          </p>
        </header>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-semibold prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-code:rounded prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 dark:prose-code:bg-gray-800"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content) }}
        />

        {/* CTA */}
        <div className="mt-12 rounded-xl border border-blue-200 bg-blue-50 p-8 text-center dark:border-blue-800 dark:bg-blue-900/20">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ready to try Echonour?
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Get 20 hours of free transcription. No credit card required.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700"
          >
            Start transcribing free
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>

        {/* Related posts */}
        <div className="mt-12 border-t border-gray-200 pt-8 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            More from the blog
          </h3>
          <div className="mt-4 space-y-4">
            {Object.values(BLOG_CONTENT)
              .filter((p) => p.slug !== post.slug)
              .slice(0, 2)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="block rounded-lg border border-gray-200 p-4 hover:border-blue-300 dark:border-gray-700 dark:hover:border-blue-700"
                >
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {relatedPost.title}
                  </h4>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {relatedPost.readTime}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </article>
    </div>
  );
}

// Simple markdown parser
function parseMarkdown(markdown: string): string {
  return markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-10 mb-4">$1</h2>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-gray-300 pl-4 italic text-gray-600 dark:border-gray-600 dark:text-gray-400">$1</blockquote>')
    // Unordered lists
    .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
    // Tables (basic)
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.some(c => c.includes('---'))) return '';
      return '<tr>' + cells.map(c => `<td class="border border-gray-300 dark:border-gray-600 px-4 py-2">${c.trim()}</td>`).join('') + '</tr>';
    })
    // Paragraphs
    .replace(/\n\n/g, '</p><p class="mb-4">')
    // Line breaks
    .replace(/\n/g, '<br />');
}
